
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, ShieldCheck, Sparkles, Brain, Sprout, Flower, Utensils, Ear, BookOpen, PersonStanding, TreeDeciduous, Activity, Search, Sun, CheckCircle2, NotebookPen } from 'lucide-react';
import YouTubeEmbed from '../components/YouTubeEmbed';

const LandingPage: React.FC = () => {
    return (
        <div className="flex flex-col">
            {/* Hero Section */}
            <section className="relative bg-sage-50 pt-20 pb-32 overflow-hidden">
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-sage-200/50 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 rounded-full bg-blue-100/50 blur-3xl"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-sage-100 mb-8 animate-fade-in-up">
                        <Sparkles size={16} className="text-yellow-500" />
                        <span className="text-sm font-medium text-sage-800">New: Voice Consultation Available</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold text-sage-900 mb-6 tracking-tight leading-tight">
                        Understand Your Body. <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-sage-600 to-teal-600">Heal Naturally.</span><br />
                        Live in Balance.
                    </h1>

                    <p className="text-xl text-sage-600 max-w-3xl mx-auto mb-10 leading-relaxed">
                        NatureNani is your AI wellness companion — rooted in <Link to="/learn/what-is-ayurveda" className="text-[#B7791F] underline decoration-[#B7791F]/30 hover:decoration-[#B7791F] underline-offset-4 transition-all">Ayurveda</Link>, a 5,000-year-old system of natural healing from India — one of the world's oldest and most complete approaches to health, using food, herbs, and lifestyle to bring your body into balance.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                        <Link
                            to="/app"
                            className="w-full sm:w-auto px-8 py-4 bg-[#2D6A4F] text-white rounded-full font-bold text-lg hover:bg-[#1B4332] transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                        >
                            Start Your Wellness Journey <ArrowRight size={20} />
                        </Link>
                        <a
                            href="#how-it-works"
                            className="w-full sm:w-auto px-8 py-4 bg-transparent text-[#2D6A4F] rounded-full font-bold text-lg hover:bg-sage-50 border-2 border-[#2D6A4F] transition-all flex items-center justify-center"
                        >
                            How Does It Work?
                        </a>
                    </div>
                    <p className="text-sm text-sage-500 italic">
                        An educational companion — designed to work alongside, never replace, your medical care.
                    </p>
                </div>
            </section>

            {/* What Makes NatureNani Different */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-12 text-center">What Makes NatureNani Different</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[
                            {
                                title: "Ayurveda as Your Foundation",
                                benefit: "Every consultation draws from classical Ayurvedic scriptures — not aggregated internet content. Your constitution is the lens through which all guidance is personalised."
                            },
                            {
                                title: "Constitution-Based Personalisation",
                                benefit: "Vata, Pitta, and Kapha respond differently to the same food, season, stress, or symptom. NatureNani's recommendations account for your specific constitution — not a generic wellness template."
                            },
                            {
                                title: "Sourced, Not Generated",
                                benefit: "Every herbal protocol cites its source text. Every recommendation is grounded in verified ancient wisdom — not AI-generated health advice or wellness trends. Your Botanical Rx traces to the chapter it came from."
                            },
                            {
                                title: "Designed to Work Alongside Your Doctor",
                                benefit: "NatureNani is an educational companion — built to complement, not compete with, your medical care. We help you understand root causes and ask better questions. Your doctor remains your most important healthcare relationship."
                            }
                        ].map((item, idx) => (
                            <div key={idx} className="flex gap-4 p-6 rounded-xl border border-sage-100 bg-sage-50/50">
                                <div className="min-w-[4px] bg-sage-400 rounded-full"></div>
                                <div>
                                    <h3 className="font-bold text-sage-900 text-lg mb-2">{item.title}</h3>
                                    <p className="text-sage-600">{item.benefit}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How NatureNani Works */}
            <section id="how-it-works" className="py-20 bg-sage-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">How NatureNani Works</h2>
                        <p className="text-sage-600 max-w-2xl mx-auto text-lg whitespace-pre-line">A conversation rooted in 5,000 years of Ayurvedic wisdom —
                            personalised to your unique mind-body constitution.</p>
                    </div>

                    {/* YouTube Video Section */}
                    <div className="max-w-4xl mx-auto mb-20 px-4">
                        <div className="bg-white/80 backdrop-blur-md border border-sage-100 rounded-[2.5rem] p-8 md:p-12 shadow-2xl shadow-sage-200/50">
                            <h3 className="text-2xl md:text-3xl font-bold text-sage-900 mb-8 text-center italic font-serif">
                                Listen: Ayurveda Explained for Modern Life
                            </h3>
                            
                            <YouTubeEmbed 
                                embedCode='<iframe width="560" height="315" src="https://www.youtube.com/embed/SYd2JHOLGJw?si=cE3XRmc8p1rzecJN" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>'
                                className="mb-10"
                            />

                            <div className="flex flex-col md:flex-row justify-center items-stretch gap-4">
                                {[
                                    "What Vata, Pitta & Kapha mean for your daily life",
                                    "The difference between your nature and your current imbalance",
                                    "How to start your own Ayurvedic wellness journey"
                                ].map((bullet, idx) => (
                                    <div key={idx} className="flex-1 bg-sage-50/80 border border-sage-100/50 rounded-2xl p-4 flex items-start gap-3 hover:bg-white transition-colors duration-300">
                                        <div className="mt-1 text-sage-500">✦</div>
                                        <p className="text-sm font-medium text-sage-800 leading-snug">{bullet}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {[
                            {
                                step: "01",
                                title: "Share Your Story",
                                description: "Tell Nani what's troubling you — symptoms, lifestyle patterns, wellness goals, or simply how you've been feeling. No forms. No clinical questionnaires. Just conversation.",
                                icon: <Activity className="w-10 h-10 text-sage-600" />
                            },
                            {
                                step: "02",
                                title: "Uncover the Root Cause",
                                description: "Nani identifies the underlying Doshic imbalance — whether Vata, Pitta, or Kapha is out of alignment — and explains what is driving your symptoms at the root, not just at the surface.",
                                icon: <Search className="w-10 h-10 text-blue-600" />
                            },
                            {
                                step: "03",
                                title: "Receive Your Protocol",
                                description: "Receive a personalised Ayurvedic protocol: herbal recommendations sourced from classical texts, a personalized wellness meal plan aligned to your constitution, and a therapeutic yoga sequence for your specific condition.",
                                icon: <Sprout className="w-10 h-10 text-green-600" />
                            },
                            {
                                step: "04",
                                title: "Build Lasting Wisdom",
                                description: "Save your protocols, track your constitution over time, and build genuine body literacy — the ability to recognise your patterns, anticipate imbalances, and restore equilibrium before symptoms take hold.",
                                icon: <Sun className="w-10 h-10 text-amber-500" />
                            }
                        ].map((item, idx) => (
                            <div key={idx} className="relative p-6 rounded-2xl bg-sage-50 border border-sage-100 hover:shadow-md transition-all">
                                <div className="text-6xl font-bold text-[#8B0000] absolute top-4 right-4 opacity-70">{item.step}</div>
                                <div className="mb-6 relative z-10">
                                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm mb-4">
                                        {item.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-sage-900 mb-3">{item.title}</h3>
                                    <p className="text-sage-600 leading-relaxed">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* The Learning Modules */}
            <section id="healing-toolkit" className="py-24 bg-sage-900 text-sage-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white tracking-tight text-center">Your Healing Toolkit</h2>
                        <p className="text-xl text-sage-200 max-w-3xl mx-auto whitespace-pre-line text-center">
                            Five integrated modules — each grounded in classical Ayurvedic, Naturopathic, and Vedic
                            wisdom, each personalised to your constitution and current state of mind.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-6">
                        {/* Consultation */}
                        <div className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-indigo-900/50 text-indigo-300">
                                    <NotebookPen className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">Consultation</h3>
                            </div>
                            <p className="text-indigo-200 font-medium mb-3">Your Ayurvedic Guide</p>
                            <p className="text-sage-100 text-sm leading-relaxed mb-4">
                                The heart of NatureNani. Describe your symptoms or concerns in plain language and Nani — your AI Ayurvedic companion — identifies the underlying Doshic imbalance, explains its root cause, and generates your full healing protocol. Voice mode available.
                            </p>
                        </div>

                        {/* Nutri Heal */}
                        <div className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-amber-900/50 text-amber-300">
                                    <Utensils className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">Nutri Heal</h3>
                            </div>
                            <p className="text-amber-200 font-medium mb-3">Food as Medicine</p>
                            <p className="text-sage-100 text-sm leading-relaxed mb-4">
                                Personalised food intelligence grounded in Ayurvedic nutrition. Rather than a rigid meal plan, Nutri Heal tells you what to eat, what to avoid, and which healing ingredients to add to your existing meals — adapted to your Dosha, your condition, and your dietary preferences. Includes curated healing recipes and herbal tonics drawn from classical texts.
                            </p>
                        </div>

                        {/* Botanical Rx */}
                        <div className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-green-900/50 text-green-300">
                                    <Sprout className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">Botanical Rx</h3>
                            </div>
                            <p className="text-green-300 font-medium mb-3">Classical Herbal Protocols</p>
                            <p className="text-sage-100 text-sm leading-relaxed mb-4">
                                Structured herbal protocols drawn directly from the Charaka Samhita — presented as a clinical table with herb name, dosage, preparation method, frequency, and therapeutic effect. Ancient scripture, made immediately actionable.
                            </p>
                        </div>

                        {/* Yoga Aid */}
                        <div className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-rose-900/50 text-rose-300">
                                    <Flower className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">Yoga Aid</h3>
                            </div>
                            <p className="text-rose-300 font-medium mb-3">Therapeutic Movement</p>
                            <p className="text-sage-100 text-sm leading-relaxed mb-4">
                                A 4-phase therapeutic protocol — Foundation, Activation, Internal Balance, Nature Integration — tailored to your specific ailment and classified as Acute (7-day) or Chronic (21-day). This is movement as medicine, not exercise.
                            </p>
                        </div>

                        {/* Manas Aid */}
                        <div className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 rounded-lg bg-[#4C3B8A] text-white">
                                    <Brain className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">Manas Aid</h3>
                            </div>
                            <p className="text-[#6B5FAD] font-medium mb-3">Ancient Wisdom for the Modern Mind</p>
                            <p className="text-sage-100 text-sm leading-relaxed mb-4">
                                Drawing from the Bhagavad Gita, the Upanishads, and the Yoga Sutras of Patanjali, Manas Aid offers shloks and spiritual guidance for mental resilience, inner balance, and purposeful living. Wisdom that has comforted humanity for 5,000 years — now personalised to your current state of mind.
                            </p>
                            <div className="mt-2">
                                <span className="inline-block px-[10px] py-[3px] bg-[#EEE8FF] text-[#4C3B8A] text-[11px] font-bold rounded-full">
                                    Evolving Feature
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Vision (Final Footer Section) */}
            <section className="py-20 bg-sage-800 text-center text-white border-t border-sage-700">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-serif italic mb-6">
                        "स्वस्थस्य स्वास्थ्य रक्षणं, आतुरस्य विकार प्रशमनं च।"
                        <br /><span className="text-xl mt-4 block text-sage-200">(Swasthasya swasthya rakshanam, aturasya vikara prashamanam cha)</span>
                    </h2>
                    <div className="text-lg text-sage-300 leading-relaxed max-w-3xl mx-auto space-y-4">
                        <p>
                            A famous quote from Charaka Samhita encapsulates the core philosophy of Ayurveda: to preserve the health of the healthy and cure the ailments of the sick. It emphasizes that maintaining health is as important as, or more important than, curing illness.
                        </p>
                        <p>
                            NatureNani exists to make this ancient mission possible for anyone, anywhere —
                            through the most personalised, authentically grounded Ayurvedic guidance that
                            modern AI can deliver.
                        </p>
                        <div className="pt-4">
                            <Link to="/about" className="inline-flex items-center text-sage-200 hover:text-white font-medium transition-colors group">
                                Meet the founder <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LandingPage;
